import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule, CardModule, FormModule, GridModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { OrderService } from '../../../../../services/order.service';
import { OrderWizardService } from '../../../../../services/order-wizard.service';
import { Product, CartItem } from '../../../../../models/product.model';
import { I18nService, TranslationModule } from 'i18n';
import { CurrencyAmountPipe, Logger } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trackingErrorCategory } from 'analytics';
import { OrderWizardTracking } from '../../order-wizard-tracking';

@Component({
  selector: 'app-product-selection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    FormModule,
    GridModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './product-selection.component.html',
  styleUrls: ['./product-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductSelectionComponent {
  protected logger = new Logger('ProductSelectionComponent');
  private orderService = inject(OrderService);
  private wizardService = inject(OrderWizardService);
  private destroyRef = inject(DestroyRef);
  private i18n = inject(I18nService);
  private tracking = inject(OrderWizardTracking, { optional: true });

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  products = signal<Product[]>([]);
  displayProducts = signal<Product[]>([]);
  cart = signal<CartItem[]>([]);
  totalPrice = signal<number>(0);

  loading = signal<boolean>(true);
  error = signal<string>('');

  requiresTopup = signal<boolean>(false);
  topupsRequired = signal<number>(0);

  expandedProductId = signal<number | null>(null);

  // Constants
  private readonly HIDDEN_PRODUCT_CODES = ['INITIAL_TOPUP'];

  // Computed
  hasProducts = computed(() => this.displayProducts().length > 0);
  canContinue = computed(() => this.cart().length > 0 && !this.loading());
  // An order is single-currency; derive it from the cart for the totals display.
  cartCurrency = computed(() => this.cart()[0]?.product?.currency || 'USDT');

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Load products, and reload when the UI language changes so language-specific
    // products (Wolfpack Assets course/bundle products) follow the active language.
    effect((onCleanup) => {
      const sub = this.i18n.currentLanguage$.subscribe(lang => this.loadProducts(lang));
      onCleanup(() => sub.unsubscribe());
    });

    // Sync cart from wizard service
    effect((onCleanup) => {
      const sub = this.wizardService.state$.subscribe(state => {
        this.cart.set(state.cart);
        this.totalPrice.set(state.totalPrice);
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private loadProducts(lang?: string): void {
    this.loading.set(true);
    this.error.set('');

    this.orderService.getProducts(lang)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.products.set(response.products);

          if (response.requires_topup) {
            this.requiresTopup.set(true);
            this.topupsRequired.set(response.topups_required || 0);
            this.displayProducts.set(this.products());
            this.wizardService.setManualTopupMode(true);
          } else {
            this.requiresTopup.set(false);
            this.topupsRequired.set(0);
            this.displayProducts.set(
              this.products().filter(p => !p.code || !this.HIDDEN_PRODUCT_CODES.includes(p.code))
            );
            this.wizardService.setManualTopupMode(false);
          }

          // Pass full product list to wizard (for auto-linking)
          this.wizardService.setProducts(response.products);
          this.loading.set(false);
        },
        error: err => {
          this.error.set('Failed to load products');
          this.loading.set(false);
          this.tracking?.step('plan', 'error', trackingErrorCategory(err));
          this.logger.error('Product load error:', err);
        }
      });
  }

  getQuantity(product: Product): number {
    return this.cart().find(i => i.product.id === product.id)?.quantity ?? 0;
  }

  isPoolProduct(product: Product): boolean {
    return !!product.code && product.code.startsWith('LIC');
  }

  isInitialTopup(product: Product): boolean {
    return product.code === 'INITIAL_TOPUP';
  }

  getMaxQuantity(product: Product): number | null {
    if (this.isPoolProduct(product)) return 1;

    if (this.requiresTopup() && this.isInitialTopup(product)) {
      return this.topupsRequired();
    }

    return null;
  }

  increaseQuantity(product: Product): void {
    const qty = this.getQuantity(product);
    const max = this.getMaxQuantity(product);

    if (max !== null && qty >= max) return;

    this.wizardService.addToCart(product, qty + 1);
  }

  decreaseQuantity(product: Product): void {
    const qty = this.getQuantity(product);
    if (qty <= 0) return;

    this.wizardService.addToCart(product, qty - 1);
  }

  setQuantity(product: Product, event: Event): void {
    const input = event.target as HTMLInputElement;
    let qty = parseInt(input.value, 10) || 0;

    if (qty < 0) qty = 0;

    const max = this.getMaxQuantity(product);
    if (max !== null && qty > max) {
      qty = max;
      input.value = qty.toString();
    }

    this.wizardService.addToCart(product, qty);
  }

  toggleDetails(product: Product): void {
    this.expandedProductId.update(id => id === product.id ? null : product.id);
  }

  isExpanded(product: Product): boolean {
    return this.expandedProductId() === product.id;
  }

  continueToPayment(): void {
    if (this.cart().length > 0) {
      this.tracking?.step('plan', 'submit');
      this.tracking?.started(this.cart().map(i => i.product.code));
      this.wizardService.nextStep();
    } else {
      this.tracking?.step('plan', 'error', 'validation');
    }
  }
}